import { useCallback, useState, useEffect } from "react";
import { Loader2, CalendarIcon, Eye, MessageSquare } from "lucide-react";
import type { DateRange as DayPickerRange } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

type DateRange = "1W" | "1M" | "MTD" | "YTD" | "All";

interface AnalyticsTabProps {
  pageId?: string;
}

export function AnalyticsTab({ pageId }: AnalyticsTabProps) {
  const [pageViewsByDay, setPageViewsByDay] = useState<
    Array<{ date: string; views: number; responses: number }>
  >([]);
  const [topCountries, setTopCountries] = useState<
    Array<{ country: string; count: number }>
  >([]);
  const [topCities, setTopCities] = useState<
    Array<{ city: string; state?: string; country?: string; count: number }>
  >([]);
  const [topReferrers, setTopReferrers] = useState<
    Array<{ referrer: string; count: number }>
  >([]);
  const [topUtmSources, setTopUtmSources] = useState<
    Array<{ utmSource: string; count: number }>
  >([]);
  const [topUtmMediums, setTopUtmMediums] = useState<
    Array<{ utmMedium: string; count: number }>
  >([]);
  const [topUtmCampaigns, setTopUtmCampaigns] = useState<
    Array<{ utmCampaign: string; count: number }>
  >([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [totalViews, setTotalViews] = useState(0);
  const [totalResponses, setTotalResponses] = useState(0);
  const [dateRange, setDateRange] = useState<DateRange>("All");
  const [customDateRange, setCustomDateRange] = useState<DayPickerRange | undefined>(undefined);
  const [tempDateRange, setTempDateRange] = useState<DayPickerRange | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const getDateRangeForPeriod = useCallback((period: DateRange): { start: Date | null; end: Date | null } => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    switch (period) {
      case "1W": {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        return { start, end };
      }
      case "1M": {
        const start = new Date(now);
        start.setMonth(start.getMonth() - 1);
        start.setHours(0, 0, 0, 0);
        return { start, end };
      }
      case "MTD": {
        const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        return { start, end };
      }
      case "YTD": {
        const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
        return { start, end };
      }
      case "All":
        return { start: null, end: null };
    }
  }, []);

  useEffect(() => {
    if (pageId) {
      setAnalyticsLoading(true);
      const { start, end } = customDateRange?.from && customDateRange?.to
        ? { start: customDateRange.from, end: customDateRange.to }
        : getDateRangeForPeriod(dateRange);
      const params = new URLSearchParams();
      if (start) params.set("startDate", start.toISOString());
      if (end) params.set("endDate", end.toISOString());
      const qs = params.toString();
      fetch(`/api/pages/${pageId}/analytics${qs ? `?${qs}` : ""}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.pageViewsByDay) setPageViewsByDay(data.pageViewsByDay);
          if (data.topCountries) setTopCountries(data.topCountries);
          if (data.topCities) setTopCities(data.topCities);
          if (data.topReferrers) setTopReferrers(data.topReferrers);
          if (data.topUtmSources) setTopUtmSources(data.topUtmSources);
          if (data.topUtmMediums) setTopUtmMediums(data.topUtmMediums);
          if (data.topUtmCampaigns) setTopUtmCampaigns(data.topUtmCampaigns);
          setTotalViews(data.totalViews ?? 0);
          setTotalResponses(data.totalResponses ?? 0);
        })
        .catch(console.error)
        .finally(() => setAnalyticsLoading(false));
    }
  }, [pageId, dateRange, customDateRange, getDateRangeForPeriod]);

  const periods: DateRange[] = ["1W", "1M", "MTD", "YTD", "All"];
  const handlePeriodClick = (period: DateRange) => {
    setDateRange(period);
    setCustomDateRange(undefined);
  };
  const handleDateRangeSelect = (range: DayPickerRange | undefined) => {
    setTempDateRange(range);
  };
  const handleDone = () => {
    setCustomDateRange(tempDateRange);
    if (tempDateRange?.from && tempDateRange?.to) {
      setDateRange("All");
    }
    setCalendarOpen(false);
  };
  const handleCancel = () => {
    setTempDateRange(customDateRange);
    setCalendarOpen(false);
  };
  const handleOpenChange = (open: boolean) => {
    setCalendarOpen(open);
    if (open) {
      setTempDateRange(customDateRange);
    }
  };
  const formatDateRange = () => {
    if (customDateRange?.from && customDateRange?.to) {
      return `${customDateRange.from.toLocaleDateString()} - ${customDateRange.to.toLocaleDateString()}`;
    }
    return "Custom Range";
  };
  const formatPeriodDateRange = (period: DateRange): string => {
    if (period === "All") return "All";
    const { start, end } = getDateRangeForPeriod(period);
    if (start && end) {
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    }
    return period;
  };

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        {periods.map((period) => (
          <Button
            key={period}
            variant={dateRange === period && !customDateRange ? "default" : "outline"}
            size="sm"
            onClick={() => handlePeriodClick(period)}
          >
            {period}
          </Button>
        ))}
        <Popover open={calendarOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant={customDateRange || (dateRange !== "All" && !customDateRange) ? "default" : "outline"}
              size="sm"
              className="gap-2"
            >
              <CalendarIcon className="h-4 w-4" />
              {customDateRange?.from && customDateRange?.to
                ? formatDateRange()
                : dateRange !== "All" && !customDateRange
                ? formatPeriodDateRange(dateRange)
                : "Custom Range"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={tempDateRange}
              onSelect={handleDateRangeSelect}
              numberOfMonths={1}
              className="rounded-md border-0"
            />
            <div className="flex justify-end gap-2 p-3 border-t">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleDone}
                disabled={!tempDateRange?.from || !tempDateRange?.to}
              >
                Done
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {analyticsLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <Eye className="h-4 w-4" />
                <span className="text-sm font-medium">Views</span>
              </div>
              <p className="text-2xl font-bold">{totalViews.toLocaleString()}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <MessageSquare className="h-4 w-4" />
                <span className="text-sm font-medium">Responses</span>
              </div>
              <p className="text-2xl font-bold">{totalResponses.toLocaleString()}</p>
            </div>
          </div>
          {pageViewsByDay.length > 0 && (
            <div className="h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={pageViewsByDay}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200" />
                  <XAxis dataKey="date" className="text-xs" />
                  <YAxis className="text-xs" allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="views"
                    name="Views"
                    stroke="var(--chart-1)"
                    fill="var(--chart-1)"
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="responses"
                    name="Responses"
                    stroke="var(--chart-2)"
                    fill="var(--chart-2)"
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Top countries
              </h3>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">
                      Country
                    </th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600">
                      Views
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topCountries.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-3 py-4 text-center text-gray-500"
                      >
                        No data
                      </td>
                    </tr>
                  ) : (
                    topCountries.map((row) => (
                      <tr
                        key={row.country}
                        className="border-t border-gray-200 hover:bg-gray-50"
                      >
                        <td className="px-3 py-2">{row.country}</td>
                        <td className="px-3 py-2 text-right font-medium">
                          {row.count.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Top cities
              </h3>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">
                      City
                    </th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600">
                      Views
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topCities.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-3 py-4 text-center text-gray-500"
                      >
                        No data
                      </td>
                    </tr>
                  ) : (
                    topCities.map((row, i) => (
                      <tr
                        key={`${row.city}-${row.state ?? ""}-${row.country ?? ""}-${i}`}
                        className="border-t border-gray-200 hover:bg-gray-50"
                      >
                        <td className="px-3 py-2">
                          {[row.city, row.state, row.country].filter(Boolean).join(", ")}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {row.count.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Top referrers
              </h3>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">
                      Referrer
                    </th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600">
                      Views
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topReferrers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-3 py-4 text-center text-gray-500"
                      >
                        No data
                      </td>
                    </tr>
                  ) : (
                    topReferrers.map((row) => (
                      <tr
                        key={row.referrer}
                        className="border-t border-gray-200 hover:bg-gray-50"
                      >
                        <td className="px-3 py-2 truncate max-w-[200px]" title={row.referrer}>
                          {row.referrer}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {row.count.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Top UTM sources
              </h3>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">
                      Source
                    </th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600">
                      Views
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topUtmSources.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-3 py-4 text-center text-gray-500"
                      >
                        No data
                      </td>
                    </tr>
                  ) : (
                    topUtmSources.map((row) => (
                      <tr
                        key={row.utmSource}
                        className="border-t border-gray-200 hover:bg-gray-50"
                      >
                        <td className="px-3 py-2 truncate max-w-[200px]" title={row.utmSource}>
                          {row.utmSource}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {row.count.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Top UTM mediums
              </h3>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">
                      Medium
                    </th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600">
                      Views
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topUtmMediums.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-3 py-4 text-center text-gray-500"
                      >
                        No data
                      </td>
                    </tr>
                  ) : (
                    topUtmMediums.map((row) => (
                      <tr
                        key={row.utmMedium}
                        className="border-t border-gray-200 hover:bg-gray-50"
                      >
                        <td className="px-3 py-2 truncate max-w-[200px]" title={row.utmMedium}>
                          {row.utmMedium}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {row.count.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Top UTM campaigns
              </h3>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">
                      Campaign
                    </th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600">
                      Views
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topUtmCampaigns.length === 0 ? (
                    <tr>
                      <td
                        colSpan={2}
                        className="px-3 py-4 text-center text-gray-500"
                      >
                        No data
                      </td>
                    </tr>
                  ) : (
                    topUtmCampaigns.map((row) => (
                      <tr
                        key={row.utmCampaign}
                        className="border-t border-gray-200 hover:bg-gray-50"
                      >
                        <td className="px-3 py-2 truncate max-w-[200px]" title={row.utmCampaign}>
                          {row.utmCampaign}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {row.count.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
